import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-grid-system';
import { Link } from 'react-router-dom';

import _ from 'lodash';
import axios from 'axios';

import { EVENT } from '../../globals';

import { useApp } from '../_libs/hooks/useApp';
import { useBrand } from '../_libs/hooks/useBrand';
import { useData } from '../_libs/hooks/useData';

import DataColumn from '../atoms/DataColumn';
import Loader from '../molecules/Loader';

const fetchRiskDriversForGeoIds = (geoIds, setRiskDriversForBlockGroups) => {
  axios
    .post('/data_risk_drivers_for_geoids/', { geoIds })
    .then((res) => {
      const data = res.data;
      if (data.status == 'OK') {
        setRiskDriversForBlockGroups(data.riskDrivers);
      }
    })
    .catch((err) => {
      console.error(err);
    });
};
interface RiskDriversForGeoIdsProp {
  geoId: number;
  featureId: number;
  featureLogicId: number;
  themeId: number;
}
interface FeaturesLogicCountProp {
  featureLogicId: number;
  count: number;
}
interface FeaturesCountProp {
  featureId: number;
  count: number;
  featureLogics: Record<number, FeaturesLogicCountProp>;
}
interface ThemesCountProp {
  themeId: number;
  count: number;
  features: Record<number, FeaturesCountProp>;
  prevalenceCount: number;
}

function findTopThemesAndFeatureLogics(riskDrivers: RiskDriversForGeoIdsProp[], limit = 3): any {
  const counts: Record<number, ThemesCountProp> = {};
  const featureLogicIds: number[] = [];
  let currGeoId = null,
    currThemeIds = [];
  _.each(riskDrivers, (riskDriver, rdi) => {    
    // Count themes
    const themeId = riskDriver.themeId;
    if (!counts[themeId]) {
      counts[themeId] = {
        themeId,
        count: 1,
        features: {},
        prevalenceCount: 0, // This will be tallied up later
      };
    } else {
      counts[themeId].count += 1;
    }
    // Count featureIds
    const features = counts[themeId].features;
    const featureId = riskDriver.featureId;
    if (!features[featureId]) {
      features[featureId] = {
        featureId,
        count: 1,
        featureLogics: {},
      };
    } else {
      features[featureId].count += 1;
    }
    // Count featureLogicIds
    const featureLogics = features[featureId].featureLogics;
    const featureLogicId = riskDriver.featureLogicId;
    if (!featureLogics[featureLogicId]) {
      featureLogics[featureLogicId] = {
        featureLogicId,
        count: 1,
      };
    } else {
      featureLogics[featureLogicId].count += 1;
    }
    // Add to array to be returned to fetch relevant featureLogic
    featureLogicIds.push(featureLogicId);

    // A single geoId may have multiple risk drivers related to the same theme.
    // Despite this that must not increase the prevalence of a theme appearing
    // within a group of block groups. Increment the last geoId's prevalence
    // count iff this is a new geoId OR the end of the risk driver list.
    const isNewGeoId = (currGeoId !== riskDriver.geoId);
    if (currGeoId === null) {
      currGeoId = riskDriver.geoId;
      currThemeIds.push(themeId);
    } else {
      // If newGeoId OR reached the end of the riskDrivers, count prevalence
      if (isNewGeoId || rdi === riskDrivers.length - 1) {
        const uniqueThemesInGeoId = _.uniq(currThemeIds);
        _.each(uniqueThemesInGeoId, (uniqueThemeId) => {
          counts[uniqueThemeId].prevalenceCount += 1;
        });
        if (isNewGeoId) {
          currGeoId = riskDriver.geoId; // Update current geoId
          currThemeIds = [themeId]; // Reset first theme of current geoId
        }
      } else {
        currThemeIds.push(themeId);
      }
    }
  });
  return {
    topThemes: _.orderBy(counts, 'prevalenceCount', 'desc').splice(0, limit),
    featureLogicIds: _.uniq(featureLogicIds),
  };
}

/**
 * 
 * NOTE: Some clinical guides feature the same list of things to consider.
 * That similarity is not taken into consideration in the DB and thus,
 * not by this function.
 * 
 * @param {array} featureLogics array of feature logic
 * @returns {number} sum of all 'things to consider' in clinical guide
 */
// function sumNumThingsToConsider (featureLogics) {
//   const clinicalGuidePDFPages = [];
//   let sum = 0;
//   _.each(featureLogics, (logic) => {
//     const pageNumber = logic.clinical_guide_pdf_page;
//     // If page hasn't already been assessed, add to sum
//     if (_.indexOf(clinicalGuidePDFPages, pageNumber) === -1) {
//       sum += logic.num_things_to_consider;
//       clinicalGuidePDFPages.push(pageNumber);
//     }
//   });
//   return sum;
// }

// interface TopRiskThemeFeatureProps {
//   count: any;
//   definition: any;
//   thingsToConsider: any;
// }
// const TopRiskThemeFeature = ({
//   definition,
// }: TopRiskThemeFeatureProps): JSX.Element => {
//   return (
//     <div>
//       <div>{definition}</div>
//     </div>
//   );
// };
interface TopRiskThemeProps {
  geoIdCount: number;
  featureData: any;
  featureLogicData: any;
  rank: number;
  themeData: any;
  theme: ThemesCountProp;
}
const TopRiskTheme = ({
  geoIdCount,
  featureData,
  featureLogicData,
  rank,
  themeData,
  theme,
}: TopRiskThemeProps): JSX.Element => {
  const { getColors, coBrand, primaryBrand } = useBrand();
  const [colors, setColors] = useState(null);
  // const [totalThingsToConsider, setTotalThingsToConsider] = useState<number>(null);
  const themeInfo = _.find(themeData, { id: theme.themeId });
  // const [topRiskThemeFeatures, setTopRiskThemeFeatures] = useState<JSX.Element[]>([]);

  useEffect(() => {
    if (getColors) {
      setColors(getColors());
    }
  }, [coBrand, primaryBrand]);

  useEffect(() => {
      // let thingsToConsider = 0;
      const _riskThemeFeatures = [];
      _.map(theme.features, (feature, i) => {
        const featureInfo = _.find(featureData, { id: feature.featureId });
        // const relevantFeatureLogics = _.filter(featureLogicData, { featureId: feature.featureId });
        // thingsToConsider += sumNumThingsToConsider(relevantFeatureLogics);
        _riskThemeFeatures.push(
          <li key={i}>{featureInfo.definition}</li>
        );
      });
      // setTotalThingsToConsider(thingsToConsider);
      // setTopRiskThemeFeatures(_riskThemeFeatures);
  }, [theme, featureData, featureLogicData]);

  return (
    <div className="px-2 py-1 br-1 border-1-solid--transparent-brand mb-4"
      style={{
        backgroundColor: '#FFF',
        color: (colors) && colors.dark,
      }}
    >
      <h4>#{rank}: {themeInfo.name}</h4>
      <div className="f-mono f-small f-bold">Prevalence</div>
      <div className="f-sans f-xl f-bold">
        {(theme.prevalenceCount / geoIdCount * 100).toFixed(2) + '%'} 
      </div>
      <div className="f-mono f-small">(n={theme.prevalenceCount})</div>
      
        {/* NOTE: Hiding for now. Could be overwhelming to prospect */}
        {/* <div>Total things to consider: {totalThingsToConsider}</div>
        <div>Features</div>
        <ul>
          { topRiskThemeFeatures }
        </ul> */}
     
    </div>
  );
};

// TODO: Add transitions
const TopRiskDriverDisplayLoader = ({
  primaryColor,
  radius,
  zipCode,
  setShowDrivers,
  setShowLoaders,
}): JSX.Element => {
  const [messageIndex, setMessageIndex] = useState(0);

  // 22-11-01: Messages via FY23Q2 JCL Loader Content https://docs.google.com/spreadsheets/d/1xLnl9_ryOxXPClUJLLEQQzsmD2ZFFzV3dr40up1YAzM/edit#gid=0
  const messages = [
    'Identifying trends across public data from US Census Bureau, USDA, HUD, EPA, CDC, NPI, and more.',
    'Modeling impactability to identify meaningful interactions across thousands of block-level features.',
    `Matching top health equity opportunities for the community within ${radius} ${(radius === 1) ? 'mile' : 'miles'} of ${zipCode}.`,
  ];

  useEffect(() => {
    if (messageIndex === messages.length) {
      setShowLoaders(false);
      setShowDrivers(true);
    } else {
      setTimeout(() => {
        setMessageIndex(messageIndex + 1);
      }, 4000);
    }
  }, [messageIndex]);

  return (
    <Row>
      <Col>
        <DataColumn>
          <div className="pa-4 f-sans t-center">
            <Loader primaryColor={primaryColor} />
            <h4>Generating Top Themes</h4>
            <div>
              { messages[messageIndex] }
            </div>
          </div>
        </DataColumn>
      </Col>
    </Row>
  );
};

const TopRiskDriverDisplay: React.FC<{ geoIds }> = ({ geoIds }) => {
  const { 
    zipCode,
    radius,
    reportData,
    riskDriverLimit,
    userId,
  } = useApp();
  const { 
    primaryBrand,
    coBrand,
    isCoBranded,
    lbBrand,
  } = useBrand();
  const {
    featureData,
    featureLogicData,
    themeData,
    fetchFeatureLogicDataByIds,
    setTopThemes,
  } = useData();

  const [riskDriversForGeoIds, setRiskDriversForGeoIds] = useState<RiskDriversForGeoIdsProp[]>(null); 

  const [showDrivers, setShowDrivers] = useState<boolean>(false);
  const [showLoaders, setShowLoaders] = useState<boolean>(false);
  const [aggregatedThemes, setAggregatedThemes] = useState(null);

  useEffect(() => {
    fetchRiskDriversForGeoIds(geoIds, setRiskDriversForGeoIds);
  }, [geoIds]);

  useEffect(() => {
    const { topThemes, featureLogicIds } = findTopThemesAndFeatureLogics(riskDriversForGeoIds, riskDriverLimit);
    
    setTopThemes(topThemes);
    setAggregatedThemes(topThemes);
    fetchFeatureLogicDataByIds(featureLogicIds);
  }, [riskDriversForGeoIds, themeData, featureData]);

  function handleShowDrivers() {
    axios
      .post('/create_event/', {
        organizationId: primaryBrand.id,
        coBrandId: coBrand.id,
        reportId: reportData.id,
        userId,
        eventCode: EVENT.UIShowTopThemes,
      });
    setShowLoaders(true);
  }

  function handleGenerateMaterials () {
    axios
      .post('/create_event/', {
        organizationId: primaryBrand.id,
        coBrandId: coBrand.id,
        reportId: reportData.id,
        userId,
        eventCode: EVENT.UIGenerateMaterials,
      });
  }

  if (showLoaders) {
    let primaryColor = lbBrand.colors.primary;
    if (primaryBrand.colors) {
      primaryColor = primaryBrand.colors.primary;
    } else if (isCoBranded && coBrand.colors) {
      primaryColor = coBrand.colors.primary;
    }
    return <TopRiskDriverDisplayLoader {...{ primaryColor, radius, zipCode, setShowDrivers, setShowLoaders }} />;
  } else if (
    showDrivers && 
    aggregatedThemes !== null && 
    themeData !== null && 
    featureData !== null && 
    featureLogicData !== null
  ) {
    return (
      <>
        <h3 className="mt-4">Top Health Equity AI Identified Themes within {radius} {(radius === 1) ? 'mile' : 'miles'} of {zipCode}</h3>
        <Row>
          {
            _.map(
              aggregatedThemes,
              (theme, it) => {
                return (
                  <Col
                    key={it}
                    sm={4}
                  >
                    <TopRiskTheme
                      geoIdCount={geoIds.length}
                      featureData={featureData}
                      featureLogicData={featureLogicData}
                      rank={parseInt(it) + 1}
                      theme={theme}
                      themeData={themeData}
                    />
                  </Col>
                );
              })
          }
        </Row>
        <Link className="button dark inline-block mb-4 mt-4 f-sans td-none" onClick={handleGenerateMaterials} to="/brand/">Generate Materials</Link>
      </>
    );
  }
  else {
    return <button className="button dark inline-block mb-4 mt-4 f-sans td-none" onClick={handleShowDrivers}>Generate Top Themes</button>;
  }
};

export default TopRiskDriverDisplay;
